import { Injectable } from '@angular/core';
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { AppState } from 'src/app/services/models/application-details.model';
import { select, Store } from '@ngrx/store';
import { selectSelectedApplication } from 'src/app/store/app-state.selectors';
import { isAppApiRequest } from './is-app-api-request';

@Injectable()
export class AppHttpInterceptor implements HttpInterceptor {

  constructor(private store: Store<AppState>) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only our own API gets the application id. A third-party host (a GCS signed URL,
    // say) has no use for it, and the custom header would force a CORS preflight that
    // the host rejects - which is exactly how it breaks file downloads.
    if (!isAppApiRequest(req.url)) {
      return next.handle(req);
    }

    // Check if the request contains the 'skipAppId' header
    if (req.headers.has('skipAppId')) {
      // If the header exists, skip adding the applicationId to the request
      // (e.g. get-apps-for-user, which resolves the tenant by email instead).
      return next.handle(req);
    }

    return this.store.pipe(
      // Get the selected applicationId from the store
      select(selectSelectedApplication),
      take(1),
      switchMap(applicationId => {
        let request = req;

        // X-Application-Id is the backend's source of truth for BOTH GET and
        // POST. Send it whenever an app is selected; a multi-app user who hasn't
        // picked yet (null) sends no header and gets no ambient app server-side.
        if (applicationId != null) {
          request = request.clone({
            setHeaders: { 'X-Application-Id': applicationId.toString() }
          });
        }

        // Keep injecting applicationId into request bodies for backward-compat.
        // It carries the SAME id as the header, so the backend's header+body
        // ownership check stays consistent (no 403 escalation mismatch).
        if (request.body) {
          let modifiedBody;

          // Handle FormData (file uploads) differently
          if (request.body instanceof FormData) {
            // For FormData, we need to append the applicationId rather than spread
            modifiedBody = new FormData();

            // Copy all existing form data entries
            request.body.forEach((value, key) => {
              modifiedBody.append(key, value);
            });

            // Add the applicationId
            modifiedBody.append('applicationId', applicationId?.toString() || '');
          } else {
            // For regular JSON bodies, use the existing spread approach
            modifiedBody = {
              ...request.body,            // Copy existing body
              applicationId: applicationId // Add applicationId to the body
            };
          }

          request = request.clone({
            body: modifiedBody
          });
        }

        // Return the modified request to continue the HTTP request handling
        return next.handle(request);
      })
    );
  }
}
